import React, { useState, useEffect } from 'react';
import editIcon from '../../../../../../Assets/images/edit-icon.svg';
import closeIcon from '../../../../../../Assets/images/close-icon.svg';
import apiClient from '../../../../../../Config/apiClient';
import Loader from '../../../../../../Context/Loader/Loader';
import ClipLoader from 'react-spinners/ClipLoader';
import './CompanyProfileEdit.scss';
import { showErrorToast, showSuccessToast } from '../../../../../../Context/Toaster/Toaster';
import { isValidEmail } from '../../../../../../Utils/Validations/validateEmail';
import { isValidCanadianPhone } from '../../../../../../Utils/Validations/validatePhone';
import { isValidCanadianPostalCode } from '../../../../../../Utils/Validations/validatePostalCode';
import { FIELD_MAX_LENGTHS } from '../../../Utils/Constants';
import { jwtDecode } from "jwt-decode";

const companyPath = process.env.REACT_APP_HS_COMPANY_BASE_PATH;
const commonPath = process.env.REACT_APP_HS_COMMON_BASE_PATH;

const EditCompanyProfileModal = ({ onClose }) => {
  const [isEditable, setIsEditable] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [provinces, setProvinces] = useState([]);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    company_name: '',
    corporate_name: '',
    address_line_1: '',
    address_line_2: '',
    province: '',
    postal_code: '',
    phone_no: '',
    company_email: '',
  });

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      const decoded = jwtDecode(token);
      setUserRole(decoded.role);
    }
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(`${companyPath}/`);
        const data = res.data.data;

        setFormData({
          company_name: data.company_name || '',
          corporate_name: data.corporate_name || '',
          address_line_1: data.address1 || '',
          address_line_2: data.address2 || '',
          province: data.province_id || '',
          postal_code: data.postal_code || '',
          phone_no: data.contact_no || '',
          company_email: data.email_id || '',
        });
      } catch (err) {
        const msg = err.response ? err.response.data.message : err.message;
        showErrorToast(msg);
      } finally {
        setLoading(false);
      }
    };

    const fetchProvinces = async () => {
      try {
        const res = await apiClient.get(`${commonPath}/list-provinces`);
        setProvinces(res.data?.data || []);
      } catch (err) {
        const msg = err.response ? err.response.data.message : err.message;
        showErrorToast(msg);
      }
    };

    fetchProfile();
    fetchProvinces();
  }, []);

  const enableFields = () => setIsEditable(true);

  const getFieldError = (name, value) => {
    if (
      ['address_line_1', 'postal_code', 'phone_no', 'province', 'company_email'].includes(name) &&
      !String(value).trim()
    ) {
      return name === 'province' ? 'Province is required.' : 'This field is required.';
    }

    if (name === 'phone_no' && value && !isValidCanadianPhone(value)) {
      return 'Enter a valid Canadian phone number.';
    }

    if (name === 'postal_code' && value && !isValidCanadianPostalCode(value)) {
      return 'Enter a valid Canadian postal code.';
    }

    if (name === 'company_email' && value && !isValidEmail(value)) {
      return 'Enter a valid email address.';
    }

    return '';
  };

  const validateForm = () => {
    const errors = {};
    Object.entries(formData).forEach(([name, value]) => {
      const error = getFieldError(name, value);
      if (error) errors[name] = error;
    });
    setFormErrors(errors);
    setSubmitted(true);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const limit = FIELD_MAX_LENGTHS[name] || (name === 'phone_no' ? FIELD_MAX_LENGTHS.contact : null);
    if (limit && value.length > limit) return;

    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const showError = (field) => submitted && formErrors[field];

  const handleUpdate = async () => {
    if (!validateForm()) return;

    setButtonLoading(true);
    try {
      const payload = {
        email_id: formData.company_email,
        address1: formData.address_line_1,
        address2: formData.address_line_2,
        province_id: formData.province,
        postal_code: formData.postal_code,
        contact_no: formData.phone_no,
      };

      const res = await apiClient.put(`${companyPath}/update`, payload);
      if (res.data.status === 'SUCCESS') {
        setIsEditable(false);
        onClose();
        showSuccessToast('Profile updated successfully!');
      }
    } catch (err) {
      if (err.response) {
        const msg = err.response ? err.response.data.message : err.message;
        showErrorToast(msg);
      } else {
        showErrorToast('Company profile update failed.');
      }
    } finally {
      setButtonLoading(false);
    }
  };

  const requiredLabel = (label) => (
    <>
      {label} <span style={{ color: 'red' }}>*</span>
    </>
  );

  return (
    <div className="cprofile-overlay" id="cprofile-modal">
      <div className={`cprofile-content editcom-profile ${isEditable ? 'edit-mode' : 'view-mode'}`}>
        {loading ? (
          <div style={{ flex: 1, height: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <ClipLoader size={50} color="grey" />
          </div>
        ) : (
          <>
            <div className="cpf-top">
              <h2>Company Profile</h2>
              {userRole === 'HS-Admin' && !isEditable && (
                <button className="profile-edit" id="ebutton" onClick={enableFields}>
                  <img src={editIcon} alt="Edit" />
                </button>
              )}
              <button className="m-close" onClick={onClose}>
                <img src={closeIcon} alt="Close" />
              </button>
            </div>
            <div>
              <div className="cprofile-inner">
                <div className="cp-form">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">{requiredLabel('Company Name')}</label>
                        <input type="text" name="company_name" className="common-input no-border-input" value={formData.company_name} disabled />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">{requiredLabel('Corporate Name')}</label>
                        <input type="text" name="corporate_name" className="common-input no-border-input" value={formData.corporate_name} disabled />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="cp-split" style={{ position: 'relative' }}>
                        <label className="fi-label">{requiredLabel('Address Line 1')}</label>
                        <input
                          type="text"
                          name="address_line_1"
                          className={`common-input ${!isEditable ? 'readonly-input' : ''} ${showError('address_line_1') ? 'error' : ''}`}
                          value={formData.address_line_1}
                          onChange={handleChange}
                          disabled={!isEditable}
                          maxLength={FIELD_MAX_LENGTHS.address}
                        />
                        {showError('address_line_1') && <span className="error-text active">{formErrors.address_line_1}</span>}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">Address Line 2</label>
                        <input
                          type="text"
                          name="address_line_2"
                          className={`common-input ${!isEditable ? 'readonly-input' : ''}`}
                          value={formData.address_line_2}
                          onChange={handleChange}
                          disabled={!isEditable}
                          maxLength={FIELD_MAX_LENGTHS.address}
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="cp-split" style={{ position: 'relative' }}>
                        <label className="fi-label">Province</label>
                        <select
                          className={`common-select ${!isEditable ? 'readonly-input' : ''} ${showError('province') ? 'error' : ''}`}
                          name="province"
                          value={formData.province}
                          onChange={handleChange}
                          disabled={!isEditable}
                        >
                          <option value="">--Select--</option>
                          {provinces.map((prov) => (
                            <option key={prov.id} value={prov.id}>
                              {prov.province_name}
                            </option>
                          ))}
                        </select>
                        {showError('province') && <span className="error-text active">{formErrors.province}</span>}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="cp-split" style={{ position: 'relative' }}>
                        <label className="fi-label">{requiredLabel('Postal Code')}</label>
                        <input
                          type="text"
                          name="postal_code"
                          className={`common-input ${!isEditable ? 'readonly-input' : ''} ${showError('postal_code') ? 'error' : ''}`}
                          value={formData.postal_code}
                          onChange={handleChange}
                          disabled={!isEditable}
                          maxLength={FIELD_MAX_LENGTHS.postalCode}
                        />
                        {showError('postal_code') && <span className="error-text active">{formErrors.postal_code}</span>}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="cp-split" style={{ position: 'relative' }}>
                        <label className="fi-label">{requiredLabel('Phone No.')}</label>
                        <input
                          type="tel"
                          name="phone_no"
                          className={`common-input ${!isEditable ? 'readonly-input' : ''} ${showError('phone_no') ? 'error' : ''}`}
                          value={formData.phone_no}
                          onChange={handleChange}
                          disabled={!isEditable}
                          maxLength={FIELD_MAX_LENGTHS.contact}
                        />
                        {showError('phone_no') && <span className="error-text active">{formErrors.phone_no}</span>}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="cp-split" style={{ position: 'relative' }}>
                        <label className="fi-label">{requiredLabel('Company Email')}</label>
                        <input
                          type="email"
                          name="company_email"
                          className={`common-input ${!isEditable ? 'readonly-input' : ''} ${showError('company_email') ? 'error' : ''}`}
                          value={formData.company_email}
                          onChange={handleChange}
                          disabled={!isEditable}
                          maxLength={FIELD_MAX_LENGTHS.email}
                        />
                        {showError('company_email') && <span className="error-text active">{formErrors.company_email}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {isEditable && (
                <div className="m-button">
                  <button className="update-button" style={{ width: '105px' }} onClick={handleUpdate} disabled={buttonLoading}>
                    {buttonLoading ? <Loader /> : 'Update'}
                  </button>

                  <button className="cancel-button me-4 float-end" onClick={() => setIsEditable(false)}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EditCompanyProfileModal;
